import { i18n } from "@/plugins/i18n"
import { formatFileSize } from "@/services/file"
import { isoDateToString } from "@/services/date"
import { rentalPeriodOf } from "@/services/rentalPeriods"

const usei18n = () => i18n.global

// A feature-set as the billing pages name it, or its code where no text exists.
const featureSetName = featureSet => {
  const key = `billing.featureSet.${String(featureSet).toLowerCase()}`
  return usei18n().te(key) ? usei18n().t(key) : featureSet
}

// What a refused delete names as still holding the record (the backend's `UsageKind`, comma-separated
// in `references`), appended to its sentence. Empty when the answer names nothing, as older ones do.
const usedIn = (value, insteadKey = "violation.usage.archive_instead") => {
  const kinds = String(value?.references ?? "").split(",").filter(Boolean)
  if (!kinds.length) return ""
  const { t, te } = usei18n()
  const names = kinds.map(kind => (te(`violation.usage.${kind}`) ? t(`violation.usage.${kind}`) : kind))
  return ` ${t("violation.usage.in", { list: names.join(", ") })} ${t(insteadKey)}`
}

// The few refused fields a form cannot rule out itself, said in the reader's words rather than the API's path: a rental's
// Zubehör (docs/suppliers.md, "Zubehör") — one its supplier no longer keeps or picked twice, too many —, its machine or
// device gone from the saved project, a trip on a tariff by time without its duration, and a supplier's accessories —
// too many, one named like another of its own (`reason: DUPLICATE`), or one for a machine the supplier has no row on
// any more (`reason: NO_ROW`; a plain `machineId` is a rental's). A rental's Zubehör is refused as `accessories`, by a
// server of before as `rentalAccessories`; a plain `accessories` is a rental's on the rental routes, else a supplier's.
// Anything else keeps the general message.
const FIELD_MESSAGES = [
  [/^machineId$/, "violation.rental.machine"],
  [/^deviceNumber$/, "violation.rental.device"],
  [/^(delivery|pickup)\.timeUnits$/, "violation.rental.time_units"],
  [/accessories\[\d+\]\.(supplierAccessoryId|supplierId)$/i, "violation.rental.accessory_unknown"],
  [/accessories\[\d+\]\.quantity$/i, "violation.rental.accessory_quantity"],
  [/^rentalAccessories$/, "violation.rental.accessories_limit"],
]
const RENTAL_ROUTE = /\/rentals(\/|$|\?)/
const fieldMessage = (value, request) => {
  if (value?.reason === "DUPLICATE" && value?.field === "name")
    return usei18n().t("violation.supplier.accessory_duplicate")
  if (value?.reason === "NO_ROW" && value?.field === "machineId")
    return usei18n().t("violation.supplier.accessory_no_row")
  if (value?.field === "accessories") {
    return usei18n().t(
      RENTAL_ROUTE.test(request?.url ?? "")
        ? "violation.rental.accessories_limit"
        : "violation.supplier.accessories_limit"
    )
  }
  const found = FIELD_MESSAGES.find(([pattern]) => pattern.test(String(value?.field ?? "")))
  return found ? usei18n().t(found[1]) : null
}

// Maps API ViolationId to a i18n user-friendly text
export const API_VIOLATIONS = {
  // An archived record still holds its name (docs/archiving.md); `archivedId` then names it, to be restored instead.
  DUPLICATE_NAME: violation =>
    violation?.archivedId
      ? usei18n().t("violation.duplicate_name_archived", { value: violation.value })
      : usei18n().t("violation.duplicate_name", { value: violation?.value }),

  // CSV Import (hard file-level failures; per-row errors are reported in the 200 response body)
  CSV_EMPTY_FILE: () => usei18n().t("violation.csv.empty_file"),
  CSV_PARSE_ERROR: () => usei18n().t("violation.csv.parse_error"),
  CSV_MISSING_REQUIRED_COLUMN: value => usei18n().t("violation.csv.missing_required_column", { value: value?.columns }),
  // Thrown without a violationValue (CsvEntityRegistry), so nothing to interpolate.
  CSV_UNKNOWN_ENTITY: () => usei18n().t("violation.csv.unknown_entity"),

  // Tenant data export (AGB 7.2). Both are informational rather than corrective — there is nothing
  // for the user to fix, only something to wait for or to request again.
  DATA_EXPORT_ALREADY_PENDING: () => usei18n().t("violation.dataexport.already_pending"),
  DATA_EXPORT_NOT_AVAILABLE: () => usei18n().t("violation.dataexport.not_available"),

  // Category
  CATEGORY_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.category.being_referenced_on_deletion"),
  // Material
  MATERIAL_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.material.being_referenced_on_deletion"),
  // Machine
  MACHINE_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.machine.being_referenced_on_deletion"),
  MACHINE_UNIT_NOT_ALLOWED: () => usei18n().t("violation.machine.unit_not_allowed"),
  SELLING_UNIT_REQUIRES_FIXED_PRICE: () => usei18n().t("violation.machine.selling_unit_requires_fixed_price"),
  HOURLY_WAGE_REPORT_MACHINE_UNIT_UNRESOLVED: () => usei18n().t("violation.machine.hourly_wage_report_unit_unresolved"),
  HOURLY_WAGE_REPORT_MACHINE_DURATION_NOT_IN_HOURS: () =>
    usei18n().t("violation.machine.hourly_wage_report_duration_not_in_hours"),
  // Hourly-wage report
  HOURLY_WAGE_REPORT_CONSTRUCTION_REPORT_REQUIRED: () =>
    usei18n().t("violation.hourlywagereport.construction_report_required"),
  // Purchase price variants
  PURCHASE_PRICE_VARIANT_LABEL_DUPLICATE: () => usei18n().t("violation.purchase_price_variant.label_duplicate"),
  PURCHASE_PRICE_VARIANT_LIMIT_EXCEEDED: () => usei18n().t("violation.purchase_price_variant.limit_exceeded"),
  REPORT_PURCHASE_PRICE_VARIANT_UNKNOWN: () => usei18n().t("violation.purchase_price_variant.report_variant_unknown"),
  // Additional-Effort
  ADDITIONAL_EFFORT_BEING_REFERENCED_ON_DELETION: () =>
    usei18n().t("violation.additionaleffort.being_referenced_on_deletion"),
  // User
  USER_BEING_REFERENCED_ON_DELETION: value =>
    usei18n().t("violation.user.being_referenced_on_deletion") + usedIn(value, "violation.usage.leave_instead"),
  USER_ALREADY_LOGIN_USER: () => usei18n().t("violation.user.already_login_user"),
  // Addresses are unique across all workspaces, not just this one — that is what lets sign-in resolve
  // the workspace from the address. The message deliberately does not say where the conflict is.
  EMAIL_ALREADY_IN_USE: () => usei18n().t("violation.user.email_already_in_use"),
  // User-Position
  USER_POSITION_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.userposition.being_referenced_on_deletion"),
  // Supplier
  SUPPLIER_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.supplier.being_referenced_on_deletion"),
  // "Eigener Betrieb": neither archived nor deleted — and, on a rental's row (`field` `variantId`), never rented from.
  SUPPLIER_BUILT_IN: value =>
    value?.field === "variantId"
      ? usei18n().t("violation.rental.own_company")
      : usei18n().t("violation.supplier.built_in"),
  SUPPLIER_KIND_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.supplier.kind_being_referenced_on_deletion"),
  SUPPLIER_ARCHIVED: () => usei18n().t("violation.supplier.archived"),
  SUPPLIER_SETUP_REQUIRED: () => usei18n().t("violation.supplier.setup_required"),
  SUPPLIER_SETUP_UNMAPPED_LABEL: () => usei18n().t("violation.supplier.setup_unmapped_label"),
  SUPPLIER_SETUP_RUNNING: () => usei18n().t("violation.supplier.setup_running"),
  PRICE_LIST_UNREADABLE: () => usei18n().t("violation.supplier.price_list_unreadable"),
  PRICE_LIST_EXPIRED: () => usei18n().t("violation.supplier.price_list_expired"),
  PRICE_LIST_APPLYING: () => usei18n().t("violation.supplier.price_list_applying"),
  // An upload while the workspace keeps as many price lists read in the last two hours as it may (409, `max`).
  PRICE_LIST_UPLOAD_LIMIT: value =>
    usei18n().t("violation.supplier.price_list_upload_limit", { max: value?.max ?? "" }),
  PURCHASE_PRICES_AT_SUPPLIER: () => usei18n().t("violation.supplier.prices_at_supplier"),
  PURCHASE_PRICE_VARIANT_SUPPLIER_REQUIRED: () => usei18n().t("violation.supplier.row_supplier_required"),
  PURCHASE_PRICE_VARIANT_SUPPLIER_DUPLICATE: () => usei18n().t("violation.supplier.row_duplicate"),
  PURCHASE_PRICE_REVISION_LAST: () => usei18n().t("violation.supplier.revision_last"),
  PURCHASE_PRICE_VARIANT_HAS_OBJECT_PRICES: () => usei18n().t("violation.supplier.row_has_object_prices"),
  PURCHASE_PRICE_VARIANT_HAS_RENTALS: () => usei18n().t("violation.supplier.row_has_rentals"),
  // A supplier's Zubehör (docs/suppliers.md, "Zubehör"): none for "Eigener Betrieb" (an archived supplier's writes are
  // SUPPLIER_ARCHIVED), and never without a price.
  SUPPLIER_ACCESSORY_NOT_ALLOWED: () => usei18n().t("violation.supplier.accessory_not_allowed"),
  SUPPLIER_ACCESSORY_LAST_PRICE: () => usei18n().t("violation.supplier.accessory_last_price"),
  // A project save taking a rented machine, or a rented device number, off the project. The device is appended, not an
  // i18n parameter.
  PROJECT_MACHINE_HAS_RENTALS: value => {
    const device = [value?.machineName, value?.deviceNumber ? `#${value.deviceNumber}` : null].filter(Boolean).join(" ")
    return device
      ? `${device} ${usei18n().t("violation.rental.still_rented")}`
      : usei18n().t("violation.rental.still_rented_unnamed")
  },
  // Archiving a project whose rental still runs (409): the rental is ended first, which an archived project still
  // allows. The answer names one running rental (`machineName`, `deviceNumber`, `deliveredOn`) and how many run
  // (`count`); the rental is appended, not an i18n parameter.
  PROJECT_HAS_RUNNING_RENTALS: value => {
    const device = [value?.machineName, value?.deviceNumber ? `#${value.deviceNumber}` : null].filter(Boolean).join(" ")
    if (!device) return usei18n().t("violation.rental.running_on_archive")
    const since = value?.deliveredOn
      ? ` ${usei18n().t("violation.rental.since")} ${isoDateToString(value.deliveredOn)}`
      : ""
    const more =
      Number(value?.count) > 1
        ? ` ${usei18n().t("violation.rental.running_more", { count: Number(value.count) - 1 })}`
        : ""
    return `${device}${since} ${usei18n().t("violation.rental.running_named")}${more}`
  },
  // Rentals (docs/suppliers.md, "Mieten"): the device is rented already in that time. The answer names the other
  // rental's days (`deliveredOn`, `pickedUpOn`); one of before only its id, whose days come from what this tab loaded
  // (`rentalPeriods.js`). Appended rather than as parameters.
  RENTAL_OVERLAPS: value => {
    const period = value?.deliveredOn
      ? { deliveredOn: value.deliveredOn, pickedUpOn: value.pickedUpOn || null }
      : rentalPeriodOf(value?.rentalId)
    if (!period) return usei18n().t("violation.rental.overlaps")
    const when = period.pickedUpOn
      ? `${isoDateToString(period.deliveredOn)} – ${isoDateToString(period.pickedUpOn)}`
      : `${usei18n().t("violation.rental.since")} ${isoDateToString(period.deliveredOn)}, ${usei18n().t("violation.rental.not_picked_up")}`
    return `${usei18n().t("violation.rental.overlaps_with")} ${when}.`
  },
  // Customer
  CUSTOMER_BEING_REFERENCED_ON_DELETION: () => usei18n().t("violation.customer.being_referenced_on_deletion"),
  CUSTOMER_CONTACT_POSITION_BEING_REFERENCED_ON_DELETION: () =>
    usei18n().t("violation.customer.contact.position.being_referenced_on_deletion"),
  // Project
  PROJECT_BEING_REFERENCED_ON_DELETION: value =>
    usei18n().t("violation.project.being_referenced_on_deletion") + usedIn(value),
  // Archived (docs/archiving.md): no new work on the project, no new assignment to the model, no login.
  PROJECT_ARCHIVED: () => usei18n().t("violation.archive.project_archived"),
  WORK_TIME_MODEL_ARCHIVED: () => usei18n().t("violation.archive.work_time_model_archived"),
  USER_ARCHIVED: () => usei18n().t("violation.archive.user_archived"),
  USER_ARCHIVE_SELF: () => usei18n().t("violation.archive.user_archive_self"),
  USER_ARCHIVE_LAST_ADMINISTRATOR: () => usei18n().t("violation.archive.user_archive_last_administrator"),

  // Construction Report Validation
  CONSTRUCTION_REPORT_PROJECT_AND_DATE_UNIQUE: () =>
    usei18n().t("violation.constructionreport.project_and_date_unique"),
  CONSTRUCTION_REPORT_AT_LEAST_ONE_USER_OR_MACHINE: () =>
    usei18n().t("violation.constructionreport.at_least_one_user_or_machine"),
  CONSTRUCTION_REPORT_MACHINE_HOURS_OUTSIDE_SHIFT: () =>
    usei18n().t("violation.constructionreport.machine_hours_outside_shift"),
  CONSTRUCTION_REPORT_BREAKS_OUTSIDE_SHIFT: () => usei18n().t("violation.constructionreport.breaks_outside_shift"),
  CONSTRUCTION_REPORT_AT_LEAST_ONE_SERVICE_OR_COMMENT_OR_OCCURRENCE: () =>
    usei18n().t("violation.constructionreport.at_least_one_service_or_comment_or_occurrence"),
  // Customer Contact
  CUSTOMER_CONTACT_DELETE_USED_REFERENCE: ({ contact }) =>
    usei18n().t("violation.customer.contact.delete_has_reference", { value: contact }),
  // Hourly Wage Invoice Generator
  HOURLY_WAGE_INVOICE_MISSING_SELLING_PRICE_FOR_USER_POSITION: ({ position }) =>
    usei18n().t("violation.hourlywageinvoice.missing_selling_price_for_user_position", { value: position }),
  HOURLY_WAGE_INVOICE_MISSING_USER_POSITION_IN_PROJECT: ({ position }) =>
    usei18n().t("violation.hourlywageinvoice.missing_user_position_in_project", { value: position }),
  // The key is `additional_effort`, as the backend sends it (HourlyWageInvoiceCreationException) —
  // reading `effort` left the name out of the message entirely.
  HOURLY_WAGE_INVOICE_MISSING_PURCHASE_PRICE_FOR_ADDITIONAL_EFFORT: value =>
    usei18n().t("violation.hourlywageinvoice.missing_price_for_additional_effort", {
      value: value?.additional_effort,
    }),
  HOURLY_WAGE_INVOICE_MISSING_PURCHASE_PRICE_FOR_MATERIAL: ({ material }) =>
    usei18n().t("violation.hourlywageinvoice.missing_purchase_price_for_material", { value: material }),
  HOURLY_WAGE_INVOICE_MISSING_PURCHASE_PRICE_FOR_MACHINE: ({ machine }) =>
    usei18n().t("violation.hourlywageinvoice.missing_purchase_price_for_machine", { value: machine }),
  HOURLY_WAGE_INVOICE_MISSING_SELLING_PRICE_FOR_MATERIAL: ({ material }) =>
    usei18n().t("violation.hourlywageinvoice.missing_selling_price_for_material", { value: material }),
  HOURLY_WAGE_INVOICE_MISSING_SELLING_PRICE_FOR_MACHINE: ({ machine }) =>
    usei18n().t("violation.hourlywageinvoice.missing_selling_price_for_machine", { value: machine }),
  // A surcharge on a row billed per hour while the machine sells per day (or the other way round) has no price to
  // go on: the project needs a fixed one (docs/suppliers.md, "Einheit je Preiszeile").
  // The machine's name as typed ("Bagger 3/5 t"), shown as text: not escaped.
  HOURLY_WAGE_INVOICE_MACHINE_UNIT_NEEDS_FIXED_PRICE: ({ machine }) =>
    usei18n().t(
      "violation.hourlywageinvoice.machine_unit_needs_fixed_price",
      { value: machine },
      { escapeParameter: false }
    ),
  HOURLY_WAGE_INVOICE_MISSING_SELLING_PRICE_FOR_ADDITIONAL_EFFORT: value =>
    usei18n().t("violation.hourlywageinvoice.missing_selling_price_for_additional_effort", {
      value: value?.additional_effort,
    }),
  PROJECT_SELLING_PRICE_ALREADY_SET: () => usei18n().t("violation.hourlywageinvoice.selling_price_already_set"),
  HOURLY_WAGE_INVOICE_CUSTOM_NUMBER_TOO_LONG: value =>
    usei18n().t("violation.hourlywageinvoice.custom_number_too_long", { max: value?.max }),
  // Settings
  // Role
  SETTINGS_ROLE_EDIT_ADMINISTRATOR: () => usei18n().t("violation.settings.role.edit_administrator"),
  SETTINGS_ROLE_DELETE_ADMINISTRATOR: () => usei18n().t("violation.settings.role.delete_administrator"),
  SETTINGS_ROLE_DELETE_USED_REFERENCE: () => usei18n().t("violation.settings.role.delete_used_reference"),
  SETTINGS_ROLE_ADMINISTRATOR_ONLY_PERMISSION: () =>
    usei18n().t("violation.settings.role.administrator_only_permission"),
  SETTINGS_ROLE_MISSING_PERMISSION_DEPENDENCY: () =>
    usei18n().t("violation.settings.role.missing_permission_dependency"),
  // Company logo
  SETTINGS_LOGO_UNSUPPORTED_TYPE: () => usei18n().t("violation.settings.logo.unsupported_type"),
  SETTINGS_LOGO_TOO_LARGE: () => usei18n().t("violation.settings.logo.too_large"),

  PROJECT_PHASE_DATE_OUTSIDE_PROJECT_RANGE: () => usei18n().t("violation.personnelplanning.date_exceeds_project_span"),

  // Leistungskatalog
  SERVICE_POSITION_IDENTIFIER_NOT_UNIQUE: () => usei18n().t("violation.serviceCatalog.position_identifier_not_unique"),
  SERVICE_POSITION_BOOKED: () => usei18n().t("violation.serviceCatalog.position_booked"),
  SERVICE_TITLE_NOT_EMPTY: () => usei18n().t("violation.serviceCatalog.title_not_empty"),
  SERVICE_CATALOG_INVALID_PARENT: () => usei18n().t("violation.serviceCatalog.invalid_parent"),
  CHANGE_ORDER_NUMBER_NOT_UNIQUE: () => usei18n().t("violation.serviceCatalog.change_order_number_not_unique"),
  CHANGE_ORDER_HAS_POSITIONS: () => usei18n().t("violation.serviceCatalog.change_order_has_positions"),
  SERVICE_ENTRY_POSITION_MISMATCH: () => usei18n().t("violation.serviceCatalog.entry_position_mismatch"),
  SERVICE_ENTRY_NOT_BOOKABLE: () => usei18n().t("violation.serviceCatalog.entry_not_bookable"),
  SERVICE_ENTRY_DUPLICATE: () => usei18n().t("violation.serviceCatalog.entry_duplicate"),
  SERVICE_COST_SPLIT_INVALID: () => usei18n().t("violation.serviceCatalog.cost_split_invalid"),
  SERVICE_POSITION_HAS_PROGRESS: () => usei18n().t("violation.serviceCatalog.position_has_progress"),
  SERVICE_PROGRESS_DATE_IN_FUTURE: () => usei18n().t("violation.serviceCatalog.progress_date_in_future"),
  SERVICE_PROGRESS_NOT_RECORDABLE: () => usei18n().t("violation.serviceCatalog.progress_not_recordable"),
  SERVICE_PROGRESS_POSITION_MISMATCH: () => usei18n().t("violation.serviceCatalog.progress_position_mismatch"),
  SERVICE_PROGRESS_DATE_TAKEN: () => usei18n().t("violation.serviceCatalog.progress_date_taken"),
  GAEB_UNSUPPORTED_FILE: () => usei18n().t("violation.serviceCatalog.gaeb_unsupported"),

  // PDF-Vorlagen. PDF_TEMPLATE_INVALID carries `path`, `reason` and sometimes `detail`; the reason picks
  // the sentence, the detail (a placeholder's name) goes into it.
  PDF_LETTERHEAD_NAME_ALREADY_EXISTS: () => usei18n().t("violation.pdf.letterhead_name_taken"),
  PDF_TEMPLATE_NAME_ALREADY_EXISTS: () => usei18n().t("violation.pdf.template_name_taken"),
  PDF_TEMPLATE_NOT_FOUND: () => usei18n().t("violation.pdf.template_not_found"),
  PDF_TEMPLATE_DOCUMENT_TYPE_MISMATCH: () => usei18n().t("violation.pdf.type_mismatch"),
  PDF_LETTERHEAD_IN_USE: value => usei18n().t("violation.pdf.letterhead_in_use", { templates: value?.templates ?? "" }),
  PDF_LETTERHEAD_IMAGE_IN_USE: () => usei18n().t("violation.pdf.image_in_use"),
  PDF_LETTERHEAD_IMAGE_LIMIT_REACHED: value => usei18n().t("violation.pdf.image_limit", { limit: value?.limit }),
  PDF_LETTERHEAD_IMAGE_UNSUPPORTED_TYPE: () => usei18n().t("violation.pdf.image_type"),
  // Refused for its file size, or for its pixels: a small file can unpack into a picture no print can hold.
  PDF_LETTERHEAD_IMAGE_TOO_LARGE: value =>
    value?.maxPixels
      ? usei18n().t("violation.pdf.image_too_many_pixels", {
          megapixels: Math.round(Number(value.maxPixels) / 1e6),
          side: value.maxSide ?? "",
        })
      : usei18n().t("violation.pdf.image_too_large"),
  PDF_TEMPLATE_SYSTEM_READ_ONLY: () => usei18n().t("violation.pdf.system_read_only"),
  PDF_TEMPLATE_INVALID: value => {
    const key = `violation.pdf.invalid.${value?.reason}`
    return usei18n().te(key)
      ? usei18n().t(key, { detail: value?.detail ?? "" })
      : usei18n().t("violation.pdf.invalid.other")
  },

  // Arbeitszeitmodelle
  // The id rides along in the response for the form to mark the offending row; the sentence does
  // not print it, because a UUID tells the reader nothing.
  WORK_TIME_MODEL_NOT_FOUND: () => usei18n().t("violation.timetracking.model_not_found"),
  WORK_TIME_MODEL_NAME_ALREADY_EXISTS: value =>
    value?.archivedId
      ? usei18n().t("violation.timetracking.model_name_archived")
      : usei18n().t("violation.timetracking.model_name_already_exists"),
  WORK_TIME_MODEL_ASSIGNMENT_INVALID: () => usei18n().t("violation.timetracking.assignment_invalid"),
  WORK_TIME_MODEL_BEING_REFERENCED_ON_DELETION: value =>
    usei18n().t("violation.timetracking.model_being_referenced_on_deletion") + usedIn(value),

  // Zeiterfassung. Every one of these is something the user can fix in the form they are looking at,
  // which is the whole point of giving them a code rather than letting the generic 400 sentence run.
  TIME_ENTRY_NOT_FOUND: () => usei18n().t("violation.timetracking.entry_not_found"),
  TIME_ENTRY_INVALID_TIME_RANGE: () => usei18n().t("violation.timetracking.invalid_time_range"),
  TIME_ENTRY_BREAK_OUTSIDE_SHIFT: () => usei18n().t("violation.timetracking.break_outside_shift"),
  TIME_ENTRY_BREAKS_OVERLAP: () => usei18n().t("violation.timetracking.breaks_overlap"),
  TIME_ENTRY_OVERLAPS_EXISTING: () => usei18n().t("violation.timetracking.overlaps_existing"),
  TIME_ENTRY_EXCEEDS_DAILY_MAXIMUM: () => usei18n().t("violation.timetracking.exceeds_daily_maximum"),
  TIME_ENTRY_ALREADY_RUNNING: () => usei18n().t("violation.timetracking.already_running"),
  TIME_ENTRY_NOT_RUNNING: () => usei18n().t("violation.timetracking.not_running"),
  // Both Pause stamps are refusable, unlike the clock-out: a refused break strands nobody, because
  // the day carries on running and the clock-out still closes it — open break and all. So they say
  // what the day's actual state is rather than failing silently.
  TIME_ENTRY_BREAK_ALREADY_RUNNING: () => usei18n().t("violation.timetracking.break_already_running"),
  TIME_ENTRY_BREAK_NOT_RUNNING: () => usei18n().t("violation.timetracking.break_not_running"),
  TIME_ENTRY_ALREADY_APPROVED: () => usei18n().t("violation.timetracking.already_approved"),
  TIME_ENTRY_EMPLOYEE_NOT_FOUND: () => usei18n().t("violation.timetracking.employee_not_found"),
  // An absence kind deleted in the settings while the review screen's absence form was open.
  TIME_ENTRY_ABSENCE_NOT_FOUND: () => usei18n().t("violation.timetracking.absence_not_found"),
  // Only reachable when somebody else entered an absence between the form's conflict check and its
  // submit; the form asks first, so this is the race, said in words.
  TIME_ENTRY_ABSENCE_OVERLAPS: () => usei18n().t("violation.timetracking.absence_overlaps"),
  // The project, not the day, is what was refused: the caller's role may not book to it (the two
  // BOOK_OWN_TIME rights). Normally unreachable — the picker only offers bookable projects — so this
  // is the answer to a right taken away while the form was open.
  TIME_ENTRY_PROJECT_NOT_BOOKABLE: () => usei18n().t("violation.timetracking.project_not_bookable"),
  TIME_ENTRY_SELF_REVIEW: () => usei18n().t("violation.timetracking.self_review"),
  // The web app never sends a location, so this can only reach it from a hand-made request; listed so
  // the code table stays complete.
  TIME_ENTRY_LOCATION_INVALID: () => usei18n().t("violation.timetracking.location_invalid"),
  // Tagesmeldungen. The detail page also words a conversion's problems with these, beside the entry.
  DAY_SUBMISSION_DATE_INVALID: () => usei18n().t("violation.daysubmission.date_invalid"),
  DAY_SUBMISSION_TOO_LARGE: () => usei18n().t("violation.daysubmission.too_large"),
  DAY_SUBMISSION_COMMENT_INVALID: () => usei18n().t("violation.daysubmission.comment_invalid"),
  DAY_SUBMISSION_COMMENT_LIMIT: () => usei18n().t("violation.daysubmission.comment_limit"),
  DAY_SUBMISSION_NOT_OPEN: () => usei18n().t("violation.daysubmission.not_open"),
  DAY_SUBMISSION_CHANGED: () => usei18n().t("violation.daysubmission.changed"),
  DAY_SUBMISSION_REPORT_BUSY: () => usei18n().t("violation.daysubmission.report_busy"),
  DAY_SUBMISSION_ENTRY_INVALID: () => usei18n().t("violation.daysubmission.entry_invalid"),
  DAY_SUBMISSION_PROJECT_MISSING: () => usei18n().t("violation.daysubmission.project_missing"),
  DAY_SUBMISSION_REPORT_CHANGED: () => usei18n().t("violation.daysubmission.report_changed"),

  CALENDAR_NOT_FOUND: () => usei18n().t("violation.personnelplanning.not_found"),
  CALENDAR_NAME_ALREADY_EXISTS: () => usei18n().t("violation.personnelplanning.name_already_exists"),
  CALENDAR_ASSIGNMENT_INVALID_DATE_RANGE: () =>
    usei18n().t("violation.personnelplanning.assignment_invalid_date_range"),
  CALENDAR_ASSIGNMENT_DATE_OUTSIDE_PROJECT_RANGE: () =>
    usei18n().t("violation.personnelplanning.assignment_date_outside_project_range"),
  CALENDAR_ASSIGNMENT_DATE_OUTSIDE_PHASE_RANGE: () =>
    usei18n().t("violation.personnelplanning.assignment_date_outside_phase_range"),
  CALENDAR_REFERENCED_PROJECT_NOT_FOUND: () => usei18n().t("violation.personnelplanning.referenced_project_not_found"),
  CALENDAR_REFERENCED_EMPLOYEE_NOT_FOUND: () =>
    usei18n().t("violation.personnelplanning.referenced_employee_not_found"),
  CALENDAR_REFERENCED_TEAM_NOT_FOUND: () => usei18n().t("violation.personnelplanning.referenced_team_not_found"),
  CALENDAR_REFERENCED_PHASE_NOT_FOUND: () => usei18n().t("violation.personnelplanning.referenced_phase_not_found"),
  CALENDAR_ASSIGNMENT_PROJECT_NOT_IN_LIST: () =>
    usei18n().t("violation.personnelplanning.assignment_project_not_in_list"),
  CALENDAR_ASSIGNMENT_EMPLOYEE_NOT_IN_LIST: () =>
    usei18n().t("violation.personnelplanning.assignment_employee_not_in_list"),
  CALENDAR_ASSIGNMENT_PHASE_NOT_FOUND: () => usei18n().t("violation.personnelplanning.assignment_phase_not_found"),
  CALENDAR_INCLUDES_ALL_PROJECTS: () => usei18n().t("violation.personnelplanning.includes_all_projects"),
  CALENDAR_BUILT_IN: () => usei18n().t("violation.personnelplanning.built_in"),
  CALENDAR_EMPLOYEE_ON_PROJECT: () => usei18n().t("violation.personnelplanning.employee_on_project"),

  // Billing — SUBSCRIPTION_REQUIRED (paywall redirect), SEAT_LIMIT_REACHED (useSeatLimitGuard) and
  // PAYMENT_DECLINED (ChangePlanView) are rendered by their own flows and need no entry here.
  BILLING_INTERVAL_UNAVAILABLE: () => usei18n().t("violation.billing.interval_unavailable"),
  // A subscribe or change of plan naming an add-on that is not on sale, such as a feature-set before its
  // release. The catalog leaves those out, so it takes a selection made from something else (the add-on
  // upsell, an old hand-off). Named, since a selection can hold several; the backend sends them as one
  // comma-separated string, as every violation value is a string. Without names this answers nothing, and
  // the 400's general wording is used rather than a sentence with a gap in it.
  BILLING_ADDON_UNAVAILABLE: value => {
    const names = String(value?.featureSets ?? "")
      .split(",")
      .filter(Boolean)
      .map(featureSetName)
    return names.length ? usei18n().t("violation.billing.addon_unavailable", { features: names.join(", ") }) : undefined
  },

  // Files. Both are refusals the store path raises before writing anything, so the message says what
  // was wrong with the file rather than that something went wrong.
  // The name, not the content type: `escapeParameter` is on globally, so an interpolated `image/png`
  // would reach the screen as `image&#x2F;png`. The name is also what the person recognises after
  // dropping a dozen files at once.
  FILE_TYPE_NOT_ALLOWED: value => usei18n().t("violation.file.type_not_allowed", { name: value?.filename }),
  FILE_STORAGE_LIMIT_REACHED: () => usei18n().t("violation.file.storage_limit_reached"),
  // The 413. The picker refuses an oversized file up front (see useUploadSizeLimit), so this is the
  // backstop for the ways round it: a stale `maxUploadBytes`, an upload started before the limit was
  // fetched, or a request that never went through a pond at all.
  FILE_TOO_LARGE: value => usei18n().t("violation.file.too_large", { size: formatFileSize(value?.maxBytes) }),
  // Kept for callers that render a violation inline. The interceptor no longer notifies on this one:
  // it redirects to the add-on upsell (FeatureLockedView), which says the same thing with a way out.
  FEATURE_NOT_ENTITLED: () => usei18n().t("violation.billing.feature_not_entitled"),

  // Rejected before any business rule ran. The response carries the offending field, but it is the API's
  // own path into the request body ("users[4].isLeading"): meaningless to the reader, and our internals
  // in their hands. Neither message repeats it — services/api.js puts it on the Sentry event instead,
  // where the trace reference shown in the snackbar leads.
  MALFORMED_REQUEST: () => usei18n().t("violation.request.malformed"),
  INVALID_FIELD: (value, request) => fieldMessage(value, request) ?? usei18n().t("violation.request.invalid_field"),

  // Pins (overview rows a user keeps on top). The kind only comes from a client bug.
  PIN_TYPE_NOT_SUPPORTED: () => usei18n().t("violation.pin.type_not_supported"),
  PIN_LIMIT_REACHED: value => usei18n().t("violation.pin.limit_reached", { max: value?.max }),
}

/** `request`: the refused request's `{ url }`, where a refusal reads differently by route (see `fieldMessage`). */
export const getViolationMessage = (errorResponse, request = null) => {
  if (errorResponse === null || errorResponse.violationCode === null) {
    console.error(`Can't create human readable violation message of input "${errorResponse}"`)
    return
  }
  const violation = API_VIOLATIONS[errorResponse.violationCode]
  if (violation === null || violation === undefined) {
    console.error(`Error reading API_VIOLATION with code ${errorResponse.violationCode}.`, errorResponse)
    return
  }
  return violation(errorResponse.violationValue, request)
}
